<template>
  <div class="dashboard__card">
    <Hints />
    <div class="dashboard__choose-org">
      <div class="dashboard__header">
        <div>
          <div
            v-if="$mq !== 'small' && $mq !== 'medium'"
            class="dashboard__header--text p1-extra-bold mb-2"
            :class="{ 'dashboard__header--text--dark': isDarkMode }"
          >
            Find organisation
          </div>

          <div class="mb-2" style="display: flex">
            <apio-button
              style="display: flex; align-items: center"
              v-if="$mq !== 'small' && $mq !== 'medium'"
              type="primary"
              class="flex mr-4"
              @click="newOrg"
              >New

              <apio-icon
                name="ui-org-new"
                class="ml-2"
                size="28"
                darkModeColour="white"
                lightModeColour="white"
              />
            </apio-button>
            <v-autocomplete
              v-model="clientHash"
              :items="organisations"
              hide-details="auto"
              item-text="name"
              item-value="clientHash"
              outlined
              width="320"
              dense
              placeholder="Search organisations..."
              @change="chooseOrg"
              ><apio-icon name="ui-search" slot="append"
                ></apio-icon
              ></v-autocomplete
            >
          </div>
        </div>
        <div v-if="$mq !== 'small' && $mq !== 'medium'">
          <div
            class="p1-extra-bold dashboard__header--text mb-2 mr-4"
            :class="{ 'dashboard__header--text--dark': isDarkMode }"
          >
            Available date range
          </div>

          <div style="display: flex">
            <apio-date-range-picker
              v-if="dashboardDateRange"
              opens="left"
              @update="updateDateRange"
              :start-date="dashboardDateRange.startDate"
              :end-date="dashboardDateRange.endDate"
            >
            </apio-date-range-picker>
          </div>
        </div>
      </div>
    </div>
    <div v-if="organisations.length == 0">
      <apio-banner
          class="ml-8 mt-4"
          :title="'Where to start?'"
          :type="'add_app'"
          :title-text-size="'h1'">
          <p class="mb-0 pb-0" slot="text">You don't have, or can't access any organisation. You can have multiple organisations, each containing several <a target="new" href="https://getapio.slite.com/p/channel/LJ2C5LbtXG73iyCWc3bXek/notes/9rdeV45jVO">apps</a>. Apps can run several interface declarations.</p>
        <apio-button button-type="secondary"
          slot="button"
          @click="newOrg"
          style="text-transform: none">Create Organisation</apio-button>
      </apio-banner>
      <apio-banner
          class="ml-8 mt-4"
          title="What can I do here?"
          type="add_app"
          title-text-size="h1">
          <p class="mb-0 pb-0" slot="text">Our documentation website has several articles and videos about what to expect. Check out <a target="new" href="https://getapio.slite.com/p/channel/LJ2C5LbtXG73iyCWc3bXek/notes/25dV_qS0as">Create an App and Deploy</a> for example.</p>
      </apio-banner>
    </div>
    <div class="dashboard__charts">
      <div class="charts-listing" v-if="organisations">
        <doughnut-chart-tile
          v-for="client in organisations"
          :client="client"
          :key="client.clientHash"
          :organisations="organisations"
          :timeStart="fromTimeUnix"
          :timeEnd="toTimeUnix"
          :hashes="[client.clientHash, '', '']"
          :interfaceMode="false"
          :running="running"
          mode="globalDashboard"
        />
      </div>
    </div>
  </div>
</template>

<script>

const compare = function (a, b) {
  const stepA = a.name.toUpperCase();
  const stepB = b.name.toUpperCase();

  let comparison = 0;
  if (stepA > stepB) {
    comparison = 1;
  } else if (stepA < stepB) {
    comparison = -1;
  }
  return comparison;
};

import { mapState, mapGetters } from "vuex";
import dayjs from "dayjs";
var utc = require("dayjs/plugin/utc"); // dependent on utc plugin
var timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);

export default {
  name: "Dashboard",
  components: {
    Hints: () => import("@/components/Hints.vue"),
    ApioBanner: () => import("@/components/Banner/Banner.vue"),
    ApioDateRangePicker: () => import("@/components/Misc/DateRangePicker.vue"),
    DoughnutChartTile: () =>
      import("@/components/DoughnutChartTile/DoughnutChartTile.vue"),
  },
  data() {
    return {
      defaultRange: true,
      search: "",
      fromDate:
        this.$route.query.f || dayjs.tz().startOf("day").utc().unix(),
      toDate: this.$route.query.t || dayjs.tz().endOf("day").utc().unix(),
      clientHash: "",
      chartDateRange: {
        startDate: this.$route.query.f
          ? dayjs
              .tz(this.$route.query.f * 1000)
              .startOf("day")
              .utc()
          : dayjs.tz().startOf("day").day(-7).utc(),
        endDate: this.$route.query.t
          ? dayjs
              .tz(this.$route.query.t * 1000)
              .endOf("day")
              .utc()
          : dayjs.tz().hour(23).endOf("day").utc()
      },
    };
  },
  computed: {
    ...mapGetters(["userClientHashList"]),
    ...mapState({
      organisations: (state) => {
        return state.dashboard.organisations.sort(compare)
      },
      loggedIn: (state) => state.auth.loggedIn,
      running: (state) => state.dashboard.running,
    }),
    fromTimeUnix() {
      return Math.floor(
        dayjs(this.chartDateRange.startDate || this.fromDate).valueOf() / 1000
      );
    },
    isDarkMode() {
      return this.$store.state.mode.darkMode;
    },
    toTimeUnix() {
      return Math.floor(
        dayjs(this.chartDateRange.endDate || this.toDate).valueOf() / 1000
      );
    },
    dashboardDateRange() {
      return this.chartDateRange;
    },
  },
  methods: {
    chooseOrg() {
      this.$router.push(
        `/organisation-dashboard?c=${this.clientHash}&f=${this.fromTimeUnix}&t=${this.toTimeUnix}`
      );
    },
    newOrg() {
      this.$router.push("/orgs/add");
    },
    updateData() {
      this.fetchData();
    },
    updateDateRange(val) {
      this.defaultRange = false
      this.$set(
        this.chartDateRange,
        "startDate",
        dayjs.tz(val.startDate).startOf("day").utc()
      );
      this.$set(
        this.chartDateRange,
        "endDate",
        dayjs.tz(val.endDate).endOf("day").utc()
      );
      this.updateData();
    },
    fetchData() {
      if (this.userClientHashList) {
        this.$store.dispatch("fetchOrganisationDashboard", {
          timeStart: this.fromTimeUnix,
          timeEnd: this.toTimeUnix,
          clientHash: this.userClientHashList,
          defaultRange: this.defaultRange,
        });
      }
    },
  },
  async beforeRouteEnter(to, from, next) {
    await next(async (vm) => {
      const orgs = await vm.$store.dispatch("fetchOrganisations");
      vm.$store.dispatch('ssoLogin', {
        clients: Array.from(orgs),
        session_id: document.cookie.replace(/(?:(?:^|.*;\s*)session\s*=\s*([^;]*).*$)|^.*$/, '$1'),
      });
      await vm.updateData();
    });
  },
};
</script>

<style lang="scss" scoped>
.dashboard {
  > .v-select {
    background: #fff;
  }

  &__choose-org {
    padding: 0 0 8px;
  }
  &__header {
    display: flex;
    padding: 0 32px;

    &--text {
      color: var(--apio-shade-2);

      &--dark {
        color: var(--apio-shade-4);
      }
    }

    justify-content: space-between;

    @media (max-width: 600px) {
      width: 100%;
      display: block;
    }
    @media (max-width: 1024px) {
      padding: 0 20px;
    }
  }
  &__org-list {
    min-width: 192px;
    max-width: 320px;
    background: white;
    @media (max-width: 600px) {
      width: 100%;
    }
  }
  &__charts {
    padding: 0 32px;
    @media (max-width: 600px) {
      display: flex;
      justify-content: left;
    }
    @media (max-width: 1024px) {
      padding: 0 20px;
    }
  }
}
</style>
